import {Button} from '@primer/react'
import {useCallback, useRef} from 'react'

import {DockedPanel, useDockedPanelActions, useIsDockedPanelOpen} from '../../contexts/DockedPanelContext'
import {useManageBatchTelemetry, type ManageBatchEntrySurface} from './manage-batch/manage-batch-telemetry'

interface ManageBatchHeaderButtonProps {
  count: number
  size?: 'small' | 'medium' | 'large'
  className?: string
  entrySurface: ManageBatchEntrySurface
}

/**
 * Green "Manage batch (N)" pill that replaces ReviewMenuButton in PR headers
 * when the unified batch is non-empty.
 *
 * Uses `openPanel` (not `togglePanel`) so clicking the button always opens or
 * brings the panel forward — dismissal is via the panel's own close button or
 * the panel switcher.
 */
export function ManageBatchHeaderButton({
  count,
  size = 'medium',
  className,
  entrySurface,
}: ManageBatchHeaderButtonProps) {
  const buttonRef = useRef<HTMLButtonElement>(null)
  const {openPanel} = useDockedPanelActions()
  const isPanelOpen = useIsDockedPanelOpen(DockedPanel.ManageBatchReviewComments)
  const {onPanelOpen} = useManageBatchTelemetry()

  const handleClick = useCallback(() => {
    openPanel(DockedPanel.ManageBatchReviewComments, {returnFocusTo: buttonRef.current})
    onPanelOpen(entrySurface)
  }, [entrySurface, openPanel, onPanelOpen])

  return (
    <Button
      ref={buttonRef}
      type="button"
      variant="primary"
      size={size}
      count={count}
      className={className}
      onClick={handleClick}
      aria-expanded={isPanelOpen}
      data-testid="manage-batch-header-button"
    >
      Manage batch
    </Button>
  )
}
